import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Menu,
  MenuItem,
  styled,
} from '@mui/material';
import {
  Search as SearchIcon,
  Apps as AppsIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { AppService, App } from '../../services/api/app';
import { format } from 'date-fns';

const PageContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100vh',
  overflow: 'auto',
  backgroundColor: theme.palette.background.default,
}));

const SearchBar = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
  },
}));

const AppCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const AppStorePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);

  const appService = useMemo(() => new AppService(), []);

  const loadApps = useCallback(async () => {
    setLoading(true);
    try {
      let data: App[] = [];
      switch (tab) {
        case 0: // My Apps
          data = await appService.getUserApps(false);
          break;
        case 1: // Organization
          data = await appService.getOrganizationApps();
          break;
        case 2: // Public
          data = await appService.getPublicApps(50);
          break;
      }
      setApps(data);
    } catch (error) {
      console.error('Failed to load apps:', error);
    } finally {
      setLoading(false);
    }
  }, [tab, appService]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, app: App) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedApp(app);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedApp(null);
  };

  const handleLaunchApp = (app: App) => {
    // Open app in new window
    const appWindow = window.open('', '_blank');
    if (appWindow) {
      appWindow.document.write(app.html_content);
      appWindow.document.close();
    }
  };

  const handleExportApp = async (app: App) => {
    try {
      const blob = await appService.exportApp(app.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${app.name.replace(/[^a-z0-9]/gi, '_')}_v${app.version}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export app:', error);
    }
    handleMenuClose();
  };

  const handleDeleteApp = async (app: App) => {
    if (window.confirm(`Are you sure you want to delete "${app.name}"?`)) {
      try {
        await appService.deleteApp(app.id);
        await loadApps();
      } catch (error) {
        console.error('Failed to delete app:', error);
      }
    }
    handleMenuClose();
  };

  const filteredApps = apps.filter((app) =>
    app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getUserId = () => {
    return localStorage.getItem('userId') || 'current-user-id';
  };

  const isOwner = (app: App) => {
    return app.user_id === getUserId();
  };

  return (
    <PageContainer>
      <div style={{ marginBottom: 24 }}>
        <Typography variant="h4" gutterBottom>
          App Store
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Browse, share, and manage HTML applications created from conversations
        </Typography>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
          <Tab label="My Apps" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Organization" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Public" icon={<PublicIcon />} iconPosition="start" />
        </Tabs>
      </div>

      <div style={{ marginBottom: 24 }}>
        <SearchBar
          fullWidth
          placeholder="Search apps..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', paddingTop: 64, paddingBottom: 64 }}>
          <Typography variant="body2" color="text.secondary">
            Loading apps...
          </Typography>
        </div>
      ) : filteredApps.length === 0 ? (
        <div style={{ textAlign: 'center', paddingTop: 64, paddingBottom: 64 }}>
          <AppsIcon style={{ fontSize: 64, color: 'rgba(0,0,0,0.6)', marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No apps found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'Try a different search term' : 'Create your first app from a conversation'}
          </Typography>
        </div>
      ) : (
        <Grid container spacing={3}>
          {filteredApps.map((app) => (
            <Grid item xs={12} sm={6} md={4} key={app.id}>
              <AppCard onClick={() => handleLaunchApp(app)}>
                <CardContent style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" gutterBottom noWrap>
                      {app.name}
                    </Typography>
                    {isOwner(app) && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, app)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {app.description || 'No description'}
                  </Typography>
                  
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                    <Chip label={`v${app.version}`} size="small" />
                    {app.is_public && <Chip label="Public" size="small" color="primary" />}
                    {app.is_organization_shared && <Chip label="Organization" size="small" color="secondary" />}
                  </Stack>
                  
                  <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    Updated {format(new Date(app.updated_at), 'MMM d, yyyy')}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<LaunchIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLaunchApp(app);
                    }}
                  >
                    Launch
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExportApp(app);
                    }}
                  >
                    Export
                  </Button>
                </CardActions>
              </AppCard>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => selectedApp && handleLaunchApp(selectedApp)}>
          <LaunchIcon fontSize="small" style={{ marginRight: 8 }} />
          Launch
        </MenuItem>
        <MenuItem onClick={() => selectedApp && handleExportApp(selectedApp)}>
          <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
          Export
        </MenuItem>
        <MenuItem>
          <ShareIcon fontSize="small" style={{ marginRight: 8 }} />
          Share
        </MenuItem>
        <MenuItem>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => selectedApp && handleDeleteApp(selectedApp)}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </PageContainer>
  );
};

export default AppStorePage;